import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Select, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CalendarClock } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { ArrangementDto, DeploymentListItemDto, Paginated, RestampResultDto } from '@pams/shared';
import { displaySlotLabel, istTimeOfDay, DEPLOYMENT_SORT_FIELDS } from '@pams/shared';
import { Attribute, CODE_CHIP } from '@/components/Attribute';
import { EmptyState } from '@/components/EmptyState';
import { FilterField } from '@/components/FilterField';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { StatusPill } from '@/components/StatusPill';
import { TwoColumnLayout } from '@/components/TwoColumnLayout';
import { useAuth } from '@/hooks/useAuth';
import { useArrangementAuthoringSetting } from '@/hooks/useArrangementAuthoringSetting';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { ARRANGEMENTS_NAV, labelOf } from '@/lib/navigation';
import { columnKeySet } from '@/lib/tableColumns';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useServerTable } from '@/hooks/useServerTable';
import { READ_ONLY_TOOLTIP, can, mayAuthorArrangement, actingOrgUnitTypeOf } from '@/lib/accessRole';
import { api, type ApiError } from '@/lib/api';
import { deploymentStatusWord, fillBadge, orderStatusWord } from '@/lib/statusPresentation';
import { queryKeys } from '@/lib/queryKeys';
import { CommandRoles } from './components/CommandRoles';
import { DutyOfficers } from './components/DutyOfficers';
import { CORRIGENDUM_COPY, DETAIL_COPY } from './copy';
import { ARCHIVE_COPY } from '@/components/archive/copy';
import { useArchiveArrangement } from '@/components/archive/useArchiveArrangement';
import { UnpublishAction } from '@/components/arrangement/UnpublishAction';
import { Attachments } from './components/Attachments';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE ARRANGEMENT DETAIL — PA_REQUIREMENTS §6.1's "Step 5, Review & send".
 *
 *  It is the canonical view of an order, which is why it owns `/arrangements/:id` and the
 *  builder moved to `/edit`. Once an order is published its dates and places are frozen
 *  (FR-5.2.14), so reading is the normal visit and editing is the exception — the opposite
 *  of a policy, which is why this is the app's first list → detail → edit shape.
 *
 *  ⚠️ IT IS WHERE DETACHING LIVES. §6.1 line 401 asks for a strip of duty dates and a
 *  per-day "this day is different"; line 369 says per-place or per-shift command is set by
 *  detaching in the review step. Publish sets `isDetached` for a slot that carried a
 *  pre-publish override; this screen is how a human sets one afterwards, and a re-stamp
 *  then skips exactly those.
 *
 *  ⚠️ `variant="detail"` PUTS THE STICKY COLUMN ON THE LEFT — the mirror of the builder,
 *  and the second consumer of that variant in the app. The identity and the actions stay in
 *  view while the duty list scrolls, because the actions act on the ORDER rather than on
 *  whichever duty happens to be on screen.
 *
 *  ⚠️ ONE `StatusPill`, on the order. Every duty row gets a `StatusDot`. A list of filled
 *  badges is a wall of colour in which the one badge that matters stops meaning anything.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3a §6] THE STORED LABEL, WITH THE REPORTING TIME IN PLACE OF "No fixed time".
 *
 *  ⚠️ `withReportingTime` AFTER `humaniseTimes`, NEVER BEFORE — the other order doubled the
 *  meridiem at noon (`From 12:00 PM PM`). `timeFormat.util.ts` carries the measurement. ⚠️ [amended
 *  C5b review] `humaniseTimes` now leaves a time carrying its meridiem alone, which closed that trap
 *  and the live `5:00 PM–11:59 AM PM` it also produced; the order is kept.
 *
 *  ⚠️ AND THE GATE IS `hasFixedTime`, NEVER `reportingAt !== null`. Every fixed-time duty carries
 *  a reporting instant — its own start since [C5b §3e], the card's earliest shift before — so a
 *  value-gate would rewrite almost every ordinary duty in the product. `withReportingTime` is total, so this is
 *  belt-and-braces rather than the only defence.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const dutyLabel = (duty: {
  slotLabel: string;
  hasFixedTime: boolean;
  reportingAt: string | null;
  /* [STAND-DOWN] So a whole-day duty with a stand-down time reads `3:00 PM–9:00 PM`. */
  standDownAt: string | null;
}): string =>
  displaySlotLabel({
    slotLabel: duty.slotLabel,
    hasFixedTime: duty.hasFixedTime,
    reportingTime: duty.reportingAt === null ? null : istTimeOfDay(duty.reportingAt),
    standDownTime: duty.standDownAt === null ? null : istTimeOfDay(duty.standDownAt),
  });

const ArrangementDetailPage = () => {
  const { arrangementId = '' } = useParams<{ arrangementId: string }>();
  const { officer } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();

  const canWrite = officer ? can(officer.accessRole, 'arrangement.write') : false;
  /*
   * ⚠️ [amended C2a §3] AUTHORING IS NARROWER THAN `arrangement.write`, AND TWO CONTROLS HERE
   * ARE AUTHORING. `Start a copy` creates an arrangement and `Update duties` re-stamps issued
   * duty rows; both are now behind `assertMayAuthorArrangement` on the server. The fill-side
   * controls on this page — detach, name, swap — stay on `canWrite`, which is the split.
   */
  const { nodalsMayAuthor } = useArrangementAuthoringSetting();
  const mayAuthor = officer
    ? mayAuthorArrangement(officer.accessRole, actingOrgUnitTypeOf(officer), nodalsMayAuthor)
    : false;
  /* ⚠️ [D4.3] WHICH DUTIES ARE SHOWING THEIR OFFICERS. Controlled, so nothing stays open
     across a page change and quietly re-fetches a sheet the operator can no longer see. */
  const [expandedDuties, setExpandedDuties] = useState<string[]>([]);
  const canDelete = officer ? can(officer.accessRole, 'arrangement.delete') : false;
  /* [D4.4 §5] The inverse transition takes the transition's own grant — see the route. */
  const canPublish = officer ? can(officer.accessRole, 'arrangement.publish') : false;

  const arrangement = useQuery({
    queryKey: queryKeys.arrangements.detail(arrangementId),
    queryFn: ({ signal }) => api.get<ArrangementDto>(`/arrangements/${arrangementId}`, { signal }),
  });

  const dutyDates = useQuery({
    queryKey: queryKeys.arrangements.deployments(arrangementId, { strip: true }),
    queryFn: ({ signal }) => api.get<{ dutyDates: string[] }>(`/arrangements/${arrangementId}/duty-dates`, { signal }),
  });

  const duties = useServerTable<DeploymentListItemDto, (typeof DEPLOYMENT_SORT_FIELDS)[number]>({
    sortFields: DEPLOYMENT_SORT_FIELDS,
    defaultSortBy: 'startsAt',
    defaultSortOrder: 'asc',
    defaultLimit: 20,
    // Two tables could share this page later; the prefix costs nothing now and prevents
    // the collision `OrgUnitDetail` had to solve after the fact.
    urlKeyPrefix: 'duties',
    /*
     * ⚠️ [D4.4 §1a] `dutyPointId` RIDES THE HOOK'S EXISTING OPAQUE-STRING FILTERS, and the
     * endpoint has accepted it since D4 — `listDeploymentsQuerySchema` declares it and
     * `listDeployments` applies it. Nothing on the web had ever set it, which is why a
     * three-card order could only ever be read as one undifferentiated list.
     */
    filterKeys: ['detachedOnly', 'dutyPointId'],
    queryKey: (params) => queryKeys.arrangements.deployments(arrangementId, params),
    queryFn: (params, signal) =>
      api.get<Paginated<DeploymentListItemDto>>(`/arrangements/${arrangementId}/deployments`, {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          ...params.filters,
        },
        signal,
      }),
    enabled: Boolean(arrangementId),
  });

  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.all });

  const detach = useMutation({
    mutationFn: (dutyId: string) => api.post<DeploymentListItemDto>(`/duties/${dutyId}/detach`, {}),
    onSuccess: () => {
      void message.success('This shift is now an exception');
      invalidate();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const restamp = useMutation({
    mutationFn: () => api.post<RestampResultDto>(`/arrangements/${arrangementId}/restamp`),
    onSuccess: (result) => {
      void message.success(DETAIL_COPY.restampDone(result.updated, result.exceptionsSkipped));
      invalidate();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  /*
   * ⚠️ [C3a §8] ONE ARCHIVE FLOW, SHARED WITH THE ARRANGEMENTS LIST. `onDone` stays this
   * page's own invalidation, because the two callers refresh different things — a shared one
   * would have to name `queryKeys.arrangements.all`, which is a PREFIX of the record on
   * screen ([P3b.3]).
   */
  const archiving = useArchiveArrangement({ onDone: invalidate });

  /*
   * ⚠️ [amended C3a §4] THE UN-PUBLISH STATE, QUERY AND MUTATION MOVED TO
   * `components/arrangement/UnpublishAction.tsx`. §4 replaced this page and would have taken
   * `Take back to draft` with it — an action [D4.4] built deliberately and §4b never asked to
   * hide — so the flow was extracted rather than copied. One implementation, two callers, and
   * this page keeps working through it, which is what keeps its own suite alive as the hidden
   * branch's coverage.
   */

  const duplicate = useMutation({
    mutationFn: () => api.post<ArrangementDto>(`/arrangements/${arrangementId}/duplicate`, {}),
    onSuccess: (copy) => {
      invalidate();
      void navigate(`/arrangements/${copy.id}/edit`);
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  /*
   * ⚠️ [C3a §8] THE ARCHIVE FLOW MOVED, AND THIS PAGE IS ITS FIRST CALLER.
   *
   * `confirmArchive`, `cascadeLine`, the two mutations and the restore gate now live in
   * `components/archive/useArchiveArrangement`, because §8 puts the same act on the
   * arrangements LIST row and transcribing eighty lines of tier branching would be two
   * vocabularies for one action — [C1e §6b] and `ConfirmDestructive`'s header both refuse it.
   * Every argument that used to sit here moved with the code it explains.
   */

  const confirmDetach = (duty: DeploymentListItemDto): void => {
    modal.confirm({
      title: DETAIL_COPY.detachConfirmTitle,
      content: DETAIL_COPY.detachConfirmBody,
      okText: 'Set apart',
      onOk: () => {
        detach.mutate(duty.id);
      },
    });
  };

  const confirmRestamp = (): void => {
    const detached = duties.query.data?.items.filter((duty) => duty.isDetached).length ?? 0;
    modal.confirm({
      title: DETAIL_COPY.restampConfirmTitle,
      content: DETAIL_COPY.restampConfirmBody(duties.total - detached, detached),
      okText: 'Update',
      onOk: () => {
        restamp.mutate();
      },
    });
  };

  const record = arrangement.data;
  const isDraft = record?.status === 'DRAFT';
  const archived = Boolean(record?.archivedAt);
  /*
   * ⚠️ [C1e §5] MAY THIS CALLER BRING IT BACK? — AND THE `DRAFT` ARM IS NOT OPTIONAL.
   *
   * `assertMayArchive` returns immediately when the origin is `DRAFT`, and `restoreArrangement`
   * passes `archived_from_status ?? status`, which is `DRAFT` for an archived draft — a draft is
   * never issued, so `archiveArrangement` never writes `CANCELLED` over it. The server therefore
   * lets ANY `arrangement.delete` holder restore a draft, which `identity.constant.ts` states in
   * terms: "a DIVISIONAL nodal admin can now archive a draft they raised".
   *
   * ⚠️ WITHOUT THIS ARM THE BUTTON WAS DISABLED FOR EXACTLY THAT RECORD, with a tooltip saying
   * only the CP office could bring it back — false — and the operator's only remaining route was
   * the purge, which destroys it. `confirmArchive` above already mirrors the short-circuit with
   * `status !== 'DRAFT'`; this is the same rule on the way back.
   */
  const mayRestore = record ? archiving.mayRestore(record) : false;

  /*
   * ⚠️ [D4.4 §1a] A DATA BRANCH, NEVER A WIDTH ONE. The card column and its filter appear
   * because the ORDER has more than one duty point — a value that is the same under test as
   * in a browser. `vitest.setup.ts` stubs `matchMedia` to `matches: false` for ever, so a
   * width branch would silently delete both from every test in the suite.
   */
  const multiPoint = (record?.dutyPointCount ?? 0) > 1;

  /*
   * ⚠️ [P6b.2] THE CARDS THE DUTY POINT FILTER IS SHOWING — the same control, read twice.
   *
   * `duties.setFilter('dutyPointId', …)` narrows the duties table SERVER-side, so the filter
   * is already the page's answer to "which card am I reading". Deriving the supervision list
   * from the same value keeps the two sections in step with no second control and no second
   * state; an unknown id (a stale URL, a card removed by an un-publish) falls back to every
   * card rather than to an empty screen — a filter must never make a record look absent.
   */
  const visibleDutyPoints = useMemo(() => {
    const picked = duties.params.filters.dutyPointId;
    const all = record?.dutyPoints ?? [];
    const one = all.filter((point) => point.id === picked);
    return one.length > 0 ? one : all;
  }, [record?.dutyPoints, duties.params.filters.dutyPointId]);

  /*
   * ⚠️ [D4.4 §5] THE SAME TWO STATUSES THE SERVICE ACCEPTS, AND FOR THE SAME REASON.
   * Naming the first officer moves an order from `PUBLISHED` to `ALLOCATION_IN_PROGRESS`,
   * so a `PUBLISHED`-only test here would hide the control for exactly the orders §5's
   * middle tier is about. The SERVER still decides; this only decides whether to offer.
   */

  const allColumns: ColumnsType<DeploymentListItemDto> = [
    {
      key: 'slot',
      title: 'Duty',
      /*
       * ⚠️ [D4.3] IT SAYS WHICH DUTY POINT PRODUCED IT. D4 added `dutyPointId`/`dutyPointTitle`
       * to this very DTO with the note "the detail page groups by it, and without it a
       * three-card order's duty list is one flat wall of rows with no way to tell which card
       * each came from" — and then nothing on the web ever read either. This is that reading.
       *
       * A LABEL rather than AntD's `rowGrouping`: the table is server-paginated, so a page
       * boundary can fall inside a group and a real group header would then claim rows that
       * are not under it. Naming the card on every row is right at any page size.
       */
      render: (_value, row) => {
        /*
         * ⚠️ NEVER BOTH, WHEN THEY ARE THE SAME WORDS. `dutyPointDisplayTitle` DERIVES the
         * title from the point's places whenever the card carries no name of its own — which
         * is the common case — so printing the title and then the route rendered
         * "Esplanade · Esplanade". That is D4.2's rule about a heading over a labelled field,
         * one level down, and the browser suite caught it where no unit test could: the
         * journey asserts the place name as EXACT text on this row.
         *
         * The route is additional information only when the operator named the card
         * something else, or when it has several stops. Then, and only then, it is appended.
         */
        const route = row.locationNames.join(' → ');
        /*
         * ⚠️ [D4.4 §1a] ONCE THE CARD HAS ITS OWN COLUMN, THIS LINE MUST NOT REPEAT IT.
         *
         * D4.2's rule — a heading over a labelled field says the same thing twice — one
         * level down. On a multi-card order the `Duty point` column names the card, so all
         * this cell owes is the time and, where it differs, the route. On a single-card
         * order there IS no column, so the second line keeps D4.3's behaviour exactly.
         */
        /*
         * ⚠️ AND WHEN THE COLUMN IS SHOWING, THE ROUTE IS DROPPED IF IT REPEATS IT TOO.
         * MEASURED by `journeyRaise` — `dutyPointDisplayTitle` derives the title FROM the
         * places, so on the ordinary one-place card the route IS the title, and printing it
         * here rendered the same words in two adjacent cells. That is D4.2's rule again:
         * the suppression follows the words, not the cell it used to live in.
         */
        const repeatsTitle = route === '' || route === row.dutyPointTitle;
        const secondary = multiPoint ? (repeatsTitle ? '' : route) : row.dutyPointTitle;
        const suffix = multiPoint ? '' : repeatsTitle ? '' : route;
        return (
          <div className="flex flex-col">
            <span className="truncate">{dutyLabel(row)}</span>
            <span className="truncate text-xs text-muted-foreground">
              {secondary}
              {suffix !== '' && <span className="text-subtle"> · {suffix}</span>}
            </span>
          </div>
        );
      },
    },
    /*
     * ─────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [D4.4 §1a] THE CARD GETS ITS OWN COLUMN. THIS WAS THE REPORTED BUG, AND IT IS A
     *  PROVENANCE DEFECT RATHER THAN AN ISOLATION ONE.
     *
     *  "Opening one arrangement shows the requirements from all the arrangements together"
     *  described THIS table. A three-card order renders as one flat, server-paginated list
     *  in which every row carries ITS OWN card's force figures — and the only marker saying
     *  which card a row came from was the second line of the `slot` cell, which D4.3
     *  correctly suppresses whenever it merely repeats the route. Since
     *  `dutyPointDisplayTitle` derives the title FROM the point's own places whenever the
     *  operator has not named the card, that suppression fires in the common case. The
     *  result was several different requirements under one heading, with nothing to say so.
     *
     *  ⚠️ IT IS NOT FR-3.6. Duty points and their requirement lines are embedded
     *  subdocuments of ONE arrangement document, `requireArrangement` spreads
     *  `arrangementScopeFilter` into the by-id read, and there is no `$lookup` anywhere in
     *  `api/src` — so a line belonging to another order cannot reach this page at all.
     *  Recorded here because "the requirements of everything" is exactly what a real
     *  isolation leak would look like, and the next reader deserves the proof rather than
     *  the conclusion.
     *
     *  ⚠️ ITS OWN HEADER CELL, never a second line inside `slot` — divergence #45. A value
     *  sharing another column's cell reads as that column's name.
     * ─────────────────────────────────────────────────────────────────────────────
     */
    ...(multiPoint
      ? [
          {
            key: 'dutyPoint',
            title: DETAIL_COPY.dutyPointColumn,
            width: 200,
            render: (_value: unknown, row: DeploymentListItemDto) => row.dutyPointTitle,
          },
        ]
      : []),
    {
      key: 'force',
      title: 'Force',
      width: 150,
      // ⚠️ FORCE ONLY. There is no command count on a duty, here or anywhere (FR-5.2.6b).
      render: (_value, row) => (
        <span className="tabular-nums">
          {row.allocatedCount} <span className="text-subtle">of {row.requiredCount}</span>
          {/* ⚠️ [P4] THE THIRD NUMBER IS A DIFFERENT STATEMENT: how many people are actually
              against it. It sits beside `allocatedCount` rather than replacing it. */}
          <span className="ml-2 text-subtle">· {row.namedCount} named</span>
        </span>
      ),
    },
    {
      key: 'fill',
      title: 'Fill',
      width: 130,
      /* ⚠️ [C5b §3] `fillBadge`, reading the row's `closedShort` — this said `All Filled` over a
         duty closed short at nobody named, because the list DTO carried no flag (D4). */
      render: (_value, row) => {
        const badge = fillBadge(row);
        return (
          <span className="inline-flex items-center gap-2">
            <StatusDot tone={badge.tone} label={badge.label} />
          </span>
        );
      },
    },
    {
      key: 'status',
      title: 'Status',
      width: 150,
      render: (_value, row) => (
        <div className="flex flex-col gap-1">
          {/* ⚠️ [C5b §3] `ALLOCATED` read `Filled` after ONE name (D8); it reads `Published` now. */}
          <StatusDot tone={deploymentStatusWord(row.status).tone} label={deploymentStatusWord(row.status).label} />
          {row.isDetached && (
            <Tooltip title={DETAIL_COPY.detach}>
              <Tag className="m-0 w-fit" data-testid={`detached-${row.slotKey}`}>
                {DETAIL_COPY.detachedFlag}
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      width: 240,
      render: (_value, row) => (
        <span className="flex items-center gap-1">
          {/*
            ⚠️ [P4] THE CP'S WAY INTO THE SAME FILL SCREEN. `ASSIGN_MYSELF` keeps the naming
            with this office, and this is where it happens — one component, one route, and
            the server decides which lines the caller is addressed on.
          */}
          <Button size="small" type="link" onClick={() => void navigate(`/duties/${row.id}`)}>
            {DETAIL_COPY.nameOfficers}
          </Button>
          {row.isDetached ? null : (
            <Tooltip title={canWrite ? DETAIL_COPY.detach : READ_ONLY_TOOLTIP}>
              <Button size="small" type="text" disabled={!canWrite} onClick={() => confirmDetach(row)}>
                This shift is different
              </Button>
            </Tooltip>
          )}
        </span>
      ),
    },
  ];

  /*
   * ⚠️ A NESTED TABLE STILL GETS A MENU, and it needs a QUALIFIER to do it safely: this page
   * has one Columns control and `/org-tree` has two, so the accessible name has to say which
   * table it drives or `getByRole('button', { name: /columns/i })` becomes ambiguous.
   */
  const columnKeys = columnKeySet(allColumns);
  const columnVisibility = useColumnVisibility({ ...columnKeys, storageKey: 'arrangement-duties' });
  const columns = allColumns.filter((column) => columnVisibility.isVisible(String(column.key)));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={CalendarClock}
        /* ⚠️ [C1f §9] THE SIDEBAR'S OWN WORD, resolved by the same function the nav uses —
           `labels.ts`'s "a second vocabulary for one screen" rule, held by construction. */
        parent={{ label: labelOf(ARRANGEMENTS_NAV, officer?.accessRole), to: ARRANGEMENTS_NAV.to }}
        title={record?.displayTitle ?? 'Arrangement'}
        subtitle={record ? `${record.reference}${record.paNumber ? ` · ${record.paNumber}` : ''}` : undefined}
      />

      <TwoColumnLayout
        variant="detail"
        secondaryLabel="Order details"
        left={
          <div className="space-y-4" data-testid="order-actions">
            <Card>
              <div className="mb-3 flex items-center justify-between gap-2">
                <span className={CODE_CHIP}>{record?.reference ?? '—'}</span>
                {/* ⚠️ THE ONE PILL. Every duty row below gets a dot instead. */}
                {/* ⚠️ [C5b §3] `orderStatusWord` — `In the Archive` here, `Archived` on the view (D9). */}
                {record && <StatusPill tone={orderStatusWord(record).tone}>{orderStatusWord(record).label}</StatusPill>}
              </div>
              <dl className="divide-y divide-border">
                {/*
                  ⚠️ [amended D4] THE ORDER DATE IS ADMINISTRATIVE, AND THE DUTY DATES SIT
                  BESIDE IT RATHER THAN INSTEAD OF IT. One order can be dated the 15th and
                  hold a card running only on the 17th; showing one number for both is
                  exactly the conflation §1e exists to end.
                */}
                <Attribute label="Order date">{record?.paDate ?? '—'}</Attribute>
                <Attribute label="Duty dates">
                  {record?.firstDutyDate
                    ? record.firstDutyDate === record.lastDutyDate
                      ? record.firstDutyDate
                      : `${record.firstDutyDate} – ${String(record.lastDutyDate)}`
                    : '—'}
                </Attribute>
                <Attribute label="Duty points">
                  <span className="tabular-nums">{record?.dutyPointCount ?? 0}</span>
                </Attribute>
                <Attribute label="Policy">
                  {/* An order may span several policies now; the count says so rather than
                      picking one and pretending. */}
                  {record?.dutyPoints[0]?.policy?.name ?? '—'}
                  {record && record.dutyPoints.length > 0 && (
                    <span className="text-subtle"> v{record.dutyPoints[0]?.policyVersionNumber ?? 1}</span>
                  )}
                  {record && new Set(record.dutyPoints.map((point) => point.policyId)).size > 1 && (
                    <span className="text-subtle">
                      {' '}
                      +{new Set(record.dutyPoints.map((point) => point.policyId)).size - 1}
                    </span>
                  )}
                </Attribute>
                <Attribute label="Places">
                  {record?.dutyPoints.flatMap((point) => point.places.map((place) => place.name)).join(', ') || '—'}
                </Attribute>
                <Attribute label="Duties">
                  <span className="tabular-nums">{record?.deploymentCount ?? 0}</span>
                </Attribute>
                <Attribute label="Shifts">
                  <span className="tabular-nums">{record?.slotCount ?? 0}</span>
                </Attribute>
                <Attribute label="Divisions">
                  {record?.divisions.map((division) => division.shortCode).join(', ') || '—'}
                </Attribute>
                {/*
                  ⚠️ [C2b §4f] THE CORRIGENDUM LINK, AND IT IS RENDERED ONLY FOR A CORRIGENDUM
                  FAMILY. A manual band already has its own header on the list; putting it here
                  too would be a second place a grouping is stated, and the two would disagree
                  the day one of them learns about a filter.

                  ⚠️ A REAL `<a>`, per `NameCell`'s rule — an operator reading a corrigendum
                  wants the order it corrects open beside it, not instead of it.
                */}
                {record?.group?.kind === 'CORRIGENDUM' && (
                  <Attribute label={record.group.role === 'PARENT' ? 'Corrected by' : 'Corrects'}>
                    {record.group.role === 'PARENT' ? (
                      <span data-testid="corrigendum-note">
                        {CORRIGENDUM_COPY.parentNote(record.group.memberCount - 1)}
                      </span>
                    ) : record.group.parent ? (
                      <Link to={`/arrangements/${record.group.parent.id}`} data-testid="corrigendum-parent">
                        {record.group.parent.paNumber ?? record.group.parent.reference}
                      </Link>
                    ) : (
                      /* ⚠️ The parent was purged. Say so — a blank reads as "not a corrigendum". */
                      <span data-testid="corrigendum-parent">{CORRIGENDUM_COPY.parentGone}</span>
                    )}
                  </Attribute>
                )}
              </dl>
            </Card>

            <div className="flex flex-wrap gap-2">
              <Tooltip title={isDraft ? DETAIL_COPY.edit : DETAIL_COPY.editFrozen}>
                <Button onClick={() => void navigate(`/arrangements/${arrangementId}/edit`)}>Edit</Button>
              </Tooltip>
              <Tooltip
                title={mayAuthor ? (canWrite ? DETAIL_COPY.restamp : READ_ONLY_TOOLTIP) : DETAIL_COPY.mayNotAuthor}
              >
                <Button
                  disabled={!canWrite || !mayAuthor || isDraft}
                  loading={restamp.isPending}
                  onClick={confirmRestamp}
                >
                  Update duties
                </Button>
              </Tooltip>
              <Tooltip
                title={mayAuthor ? (canWrite ? DETAIL_COPY.duplicate : READ_ONLY_TOOLTIP) : DETAIL_COPY.mayNotAuthor}
              >
                <Button
                  disabled={!canWrite || !mayAuthor}
                  loading={duplicate.isPending}
                  onClick={() => duplicate.mutate()}
                >
                  Start a copy
                </Button>
              </Tooltip>
              {/*
                ⚠️ [P6c §6] A REAL `<a>`, VIA `href`, NOT `navigate()`.

                `NameCell`'s rule: anything that NAVIGATES renders an anchor, so ⌘-click opens
                the sheet in its own tab and the link can be copied. That matters more here
                than anywhere — an operator printing an order wants it in a second tab beside
                the one they are working in, not instead of it.

                ⚠️ AND IT IS OFFERED ON EVERY STATUS, INCLUDING ARCHIVED. An order that was
                issued and then cancelled is exactly the one somebody needs a printed copy of;
                the endpoint deliberately does not call `assertNotArchived` for the same reason.
              */}
              <Tooltip title={DETAIL_COPY.print}>
                <Button
                  href={`/arrangements/${arrangementId}/print`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="print-order"
                >
                  Print order
                </Button>
              </Tooltip>
              {/*
                ⚠️ [D4.4 §5] OFFERED ONLY ON AN ISSUED, UNARCHIVED ORDER. A draft has nothing
                to take back and an archived one is reached through Restore — offering it on
                either would be a control that exists to be refused.

                ⚠️ IT SITS BESIDE `Edit` RATHER THAN BESIDE `Delete`, because that is what it
                is FOR: `Edit` is disabled-in-spirit on a published order (its dates and
                places are frozen), and this is the door to changing them. Putting it next to
                the destructive action would read as a lesser kind of deletion, which is
                exactly the confusion the archive-versus-un-publish split exists to prevent.
              */}
              {/*
                ⚠️ [amended C3a §4] THE SHARED CONTROL. §4 replaced this page with
                `pages/ArrangementView` and would have taken `Take back to draft` with it, so the
                flow was EXTRACTED — one implementation, two callers, exactly as
                `useArchiveArrangement` was earlier in the same pass. It computes `canUnpublish`
                itself and renders nothing when the record cannot be taken back.
              */}
              <UnpublishAction
                arrangementId={arrangementId}
                status={record?.status}
                archived={archived}
                canPublish={canPublish}
                onDone={invalidate}
              />
              {archived ? (
                /*
                  ⚠️ [C1e §5] RESTORE ASKS THE SAME QUESTION BACKWARDS, and the RECORD answers
                  it — `archivedTierOf(record.status)`, the SAME shared function the server
                  authorises on, so there is one derivation rather than two. A CP-office nodal
                  may un-FILE what they filed and may not un-CANCEL what a super admin
                  withdrew.

                  ⚠️ IT REUSES THE TOOLTIP THAT IS ALREADY THERE and adds no overlay site —
                  `e2e/fixtures/overlays.ts` pins this file at exactly seven `Tooltip`s and
                  `census.spec.ts` fails on an eighth.
                */
                <Tooltip title={mayRestore ? ARCHIVE_COPY.restore : ARCHIVE_COPY.restoreNotYours}>
                  <Button
                    disabled={!canDelete || !mayRestore}
                    loading={archiving.isRestoring}
                    onClick={() => {
                      if (record) archiving.restore(record);
                    }}
                  >
                    Restore
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  danger
                  disabled={!canDelete}
                  loading={archiving.isArchiving}
                  onClick={() => {
                    if (record) void archiving.confirmArchive(record);
                  }}
                >
                  Archive
                </Button>
              )}
            </div>

            {/*
              ⚠️ [C2b §3] IN THE STICKY IDENTITY COLUMN, BESIDE THE ACTIONS, NOT IN THE RIGHT
              RAIL. A file pinned to an order is part of what the order IS — the route map an
              operator prints with the sheet — where the right rail holds views of the duties.
              It renders on EVERY status including archived, for `print-order`'s reason: a
              cancelled order is exactly the one somebody needs the paperwork from.
            */}
            <Attachments arrangementId={arrangementId} canWrite={canWrite} />
          </div>
        }
        right={
          <div className="space-y-4">
            {/* ⚠️ §6.1's DEPLOYMENT STRIP — the dates this order covers, at a glance. */}
            {(dutyDates.data?.dutyDates.length ?? 0) > 0 && (
              <Card size="small" data-testid="duty-strip">
                <p className="mb-2 text-xs text-muted-foreground">{DETAIL_COPY.strip}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(dutyDates.data?.dutyDates ?? []).map((date) => (
                    <Tag key={date} className="m-0">
                      {date}
                    </Tag>
                  ))}
                </div>
              </Card>
            )}

            {/*
              ⚠️ [FIX-P6b.1 · amended P6b.2] SUPERVISION, WHICH THIS PAGE COLLECTED AND NEVER
              SHOWED BACK — NOW ONE CARD PER DUTY POINT RATHER THAN ONE PANEL PER ORDER.

              It sits above the duties table because supervision belongs to the CARD, where a
              row below is one duty; an order with twenty-four duty points has twenty-four
              answers to "who is in charge here" and one panel could only ever give a list
              with no place to put the ones that have none. It is inert on a draft only in the
              sense that a draft may simply have none — command entries exist from the first
              save, unlike the duties, which are materialised at publish.

              ⚠️ AND IT FOLLOWS THE DUTY POINT FILTER, so the two sections answer for the SAME
              card. The filter is the page's one "which card am I reading" control; a
              supervision list that ignored it would show a commander for a card whose duties
              are not on screen, which is the multi-card confusion the filter exists to end.
            */}
            {record && <CommandRoles dutyPoints={visibleDutyPoints} />}

            <div>
              {/* There is no filter row on this page, so the control sits on the heading
                  line beside the hint it belongs to. */}
              <div className="mb-2 flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold tracking-tight">{DETAIL_COPY.dutiesTitle}</h2>
                  <p className="text-xs text-muted-foreground">
                    {multiPoint ? DETAIL_COPY.dutiesMultiPointHint : DETAIL_COPY.dutiesHint}
                  </p>
                </div>
                {/*
                  ⚠️ [D4.4 §1a] NAMING THE CARD ON EVERY ROW IS THE FIX; THIS IS THE
                  AFFORDANCE ON TOP OF IT. A label alone is right at any page size — which is
                  why D4.3 chose one over AntD's `rowGrouping`, whose header would claim rows
                  on the far side of a page boundary — but on a long order the operator also
                  wants to read ONE card's duties. The filter is server-side, so it narrows
                  the whole order rather than the page in front of them.
                */}
                {multiPoint && (
                  <FilterField label={DETAIL_COPY.dutyPointFilter} className="w-56">
                    <Select
                      allowClear
                      className="w-full"
                      placeholder={DETAIL_COPY.allDutyPoints}
                      value={duties.params.filters.dutyPointId || undefined}
                      onChange={(value?: string) => duties.setFilter('dutyPointId', value)}
                      options={(record?.dutyPoints ?? []).map((point) => ({
                        value: point.id,
                        label: point.displayTitle,
                      }))}
                      data-testid="duty-point-filter"
                    />
                  </FilterField>
                )}
                <ColumnsMenu
                  columns={allColumns}
                  visibility={columnVisibility}
                  locked={columnKeys.locked}
                  qualifier="duties"
                  testId="columns-menu-duties"
                />
              </div>
              <Table
                {...duties.tableProps}
                columns={columns}
                size="small"
                scroll={{ x: 'max-content' }}
                /*
                 * ⚠️ [D4.3 §5] WHO IS ACTUALLY ON THIS DUTY, ONE ROW DOWN.
                 *
                 * `expandedRowRender` MOUNTS ONLY WHAT IS OPEN, which is the whole reason the
                 * officer surface can live on a list: a published order has hundreds of
                 * duties, and each open one costs a `GET /duties/:id/fill`. Reading them all
                 * eagerly would be exactly the N+1 this page has avoided everywhere else.
                 *
                 * ⚠️ A DRAFT HAS NO DUTIES AT ALL — they are materialised at publish — so
                 * this is inert on one rather than hidden by a branch.
                 */
                /*
                 * ⚠️ [amended C2a §4] RESTORED. It was commented out wholesale by `4099115`,
                 * behind a `//commented expadRowRender` marker, with the rationale above left
                 * standing — so this page silently stopped answering "who is on this duty" for
                 * every role, while the comment went on describing behaviour that no longer
                 * happened. That is [P6b.2]'s rule inverted: a screen that COLLECTED a field
                 * and stopped showing it back, with nothing failing.
                 *
                 * ⚠️ `pnpm check:comments` CANNOT SEE THIS. Its two gates cover a commented-out
                 * middleware registration and a block in `services/` or `middlewares/`; a
                 * commented-out JSX prop in `web/` matches neither. What surfaced it was
                 * `tsc` — `DutyOfficers`, `expandedDuties` and `setExpandedDuties` all became
                 * unused — which is why an unused-symbol error is worth reading rather than
                 * silencing.
                 */
                expandable={{
                  expandedRowRender: (row: DeploymentListItemDto) => (
                    <DutyOfficers dutyId={row.id} readOnly={!canWrite} />
                  ),
                  expandedRowKeys: expandedDuties,
                  onExpandedRowsChange: (keys) => {
                    setExpandedDuties(keys.map(String));
                  },
                  columnTitle: <span className="sr-only">{DETAIL_COPY.officersHeading}</span>,
                }}
                locale={{ emptyText: <EmptyState size="sm" title={DETAIL_COPY.dutiesEmpty} /> }}
              />
            </div>
          </div>
        }
      />
    </div>
  );
};

export default ArrangementDetailPage;
